import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  IconButton,
  Typography,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { getMemberTasks } from "../../../redux/actions/authActions";
import { useDispatch, useSelector } from "react-redux";
import { Tag, Popover, Tooltip as AntTooltip, Modal, Input } from "antd";
import { EnvironmentOutlined, SearchOutlined } from "@ant-design/icons";

// Helper to extract and normalize all locations from a task row
const parseTaskLocations = (row) => {
  let list = [];
  if (row?.locations && Array.isArray(row.locations) && row.locations.length > 0) {
    list = row.locations.filter(
      (l) => (l.state && l.state.trim()) || (l.location && l.location.trim())
    );
  }
  if (list.length === 0 && (row?.state?.trim() || row?.location?.trim())) {
    list = [{ state: row.state?.trim() || "", location: row.location?.trim() || "" }];
  }
  return list;
};

// Helper to group locations by state
const groupLocationsByState = (locations) => {
  const grouped = {};
  locations.forEach((l) => {
    const st = l.state?.trim() || "Unspecified State";
    const loc = l.location?.trim() || "All Branches";
    if (!grouped[st]) grouped[st] = [];
    if (!grouped[st].includes(loc)) grouped[st].push(loc);
  });
  return grouped;
};

const MemberTaskHome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  
  const memberId = searchParams.get("memberId");
  const { member_tasks } = useSelector((state) => state.auth);
  
  const [rows, setRows] = useState([]);
  const [locationModalData, setLocationModalData] = useState(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (token && memberId) {
          await dispatch(getMemberTasks(memberId));
        }
      } catch (err) {
        console.error("Error fetching tasks:", err);
      }
    };
    fetchData();
  }, [dispatch, memberId, token]);

  useEffect(() => {
    if (member_tasks) {
      setRows(member_tasks);
    }
  }, [member_tasks]);

  const handleViewTask = (clientId, taskId) => {
    navigate(`/team-management/member-task/view?clientId=${clientId}&taskId=${taskId}`);
  };

  // Logic for dynamic status colors
  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending":
        return { color: "#af7826ff", fontWeight: "bold" }; // Orange
      case "Completed":
        return { color: "#155e17ff", fontWeight: "bold" }; // Green
      case "In Progress":
        return { color: "#2196f3", fontWeight: "bold" }; // Blue
      default:
        return { fontWeight: "bold" };
    }
  };

  const columns = [
    { field: "id", headerName: "Task ID", width: 150 },
    {
      field: "service",
      headerName: "Task Name",
      width: 220,
      renderCell: (params) => params.value?.name || "N/A",
    },
    {
      field: "state",
      headerName: "State",
      width: 170,
      renderCell: (params) => {
        const row = params.row;
        let states = [];
        if (row.locations && Array.isArray(row.locations) && row.locations.length > 0) {
          states = [...new Set(row.locations.map((l) => l.state?.trim()).filter(Boolean))];
        } else if (row.state?.trim()) {
          states = [row.state.trim()];
        }

        if (states.length === 0) return <span>-</span>;
        if (states.length <= 2) {
          return (
            <div className="flex flex-wrap gap-1 items-center h-full py-1">
              {states.map((st, i) => (
                <Tag key={i} color="blue" className="!mr-1 !mb-0 text-xs">
                  {st}
                </Tag>
              ))}
            </div>
          );
        }

        const taskName = params.row.service?.name || params.row.name || "Task";
        const locations = parseTaskLocations(row);

        return (
          <div className="flex flex-wrap gap-1 items-center h-full py-1">
            {states.slice(0, 2).map((st, i) => (
              <Tag key={i} color="blue" className="!mr-1 !mb-0 text-xs">
                {st}
              </Tag>
            ))}
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div style={{ minWidth: 240, maxWidth: 300 }} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between pb-1.5 mb-2 border-b border-gray-100">
                    <span className="font-bold text-xs text-gray-700">All States ({states.length})</span>
                    <button
                      type="button"
                      onClick={() => {
                        setLocationSearchQuery("");
                        setLocationModalData({ taskName, locations });
                      }}
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold cursor-pointer underline"
                    >
                      Full Table
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto">
                    {states.map((st, i) => (
                      <Tag key={i} color="blue" className="text-xs">
                        {st}
                      </Tag>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="processing" className="!mr-0 !mb-0 text-xs font-semibold cursor-pointer hover:opacity-80">
                +{states.length - 2} more
              </Tag>
            </Popover>
          </div>
        );
      },
    },
    {
      field: "location",
      headerName: "Location / Cities",
      width: 220,
      renderCell: (params) => {
        const row = params.row;
        const locations = parseTaskLocations(row);
        const validBranches = locations.filter((l) => l.location?.trim() || l.state?.trim());

        if (validBranches.length === 0) return <span>-</span>;
        if (validBranches.length <= 2) {
          return (
            <div className="flex flex-wrap gap-1 items-center h-full py-1">
              {validBranches.map((l, i) => (
                <AntTooltip
                  key={i}
                  title={l.state ? `${l.location || 'Branch'} (${l.state})` : (l.location || l.state)}
                >
                  <Tag color="cyan" className="!mr-1 !mb-0 text-xs">
                    {l.location || l.state}
                  </Tag>
                </AntTooltip>
              ))}
            </div>
          );
        }

        const taskName = params.row.service?.name || params.row.name || "Task";
        const grouped = groupLocationsByState(validBranches);

        return (
          <div className="flex flex-wrap gap-1 items-center h-full py-1">
            {validBranches.slice(0, 2).map((l, i) => (
              <AntTooltip
                key={i}
                title={l.state ? `${l.location || 'Branch'} (${l.state})` : (l.location || l.state)}
              >
                <Tag color="cyan" className="!mr-1 !mb-0 text-xs">
                  {l.location || l.state}
                </Tag>
              </AntTooltip>
            ))}
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div style={{ minWidth: 260, maxWidth: 320 }} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between pb-1.5 mb-2 border-b border-gray-100">
                    <span className="font-bold text-xs text-gray-700">Cities & Branches ({validBranches.length})</span>
                    <button
                      type="button"
                      onClick={() => {
                        setLocationSearchQuery("");
                        setLocationModalData({ taskName, locations });
                      }}
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold cursor-pointer underline"
                    >
                      Full Table
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {Object.entries(grouped).map(([st, cList], idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-100">
                        <div className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1">
                          {st} ({cList.length})
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cList.map((city, cIdx) => (
                            <Tag key={cIdx} color="default" className="text-xs bg-white">
                              {city}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="geekblue" className="!mr-0 !mb-0 text-xs font-bold cursor-pointer hover:opacity-80">
                +{validBranches.length - 2} more
              </Tag>
            </Popover>
          </div>
        );
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 180,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', whiteSpace: 'nowrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value ? new Date(params.value).toDateString() : "No Date"}
          </Typography>
        </div>
      ),
    },
    {
      field: "client",
      headerName: "Client Name",
      width: 180,
      renderCell: (params) => params.value?.name || "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <div style={getStatusStyle(params.value)}>{params.value}</div>
      ),
    },
    {
      field: "view",
      headerName: "View",
      width: 80,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleViewTask(params.row.client?._id, params.row._id)}
        >
          <Visibility />
        </IconButton>
      ),
    },
  ];

  const filteredModalLocations = React.useMemo(() => {
    if (!locationModalData?.locations) return [];
    if (!locationSearchQuery.trim()) return locationModalData.locations;
    const q = locationSearchQuery.toLowerCase();
    return locationModalData.locations.filter(
      (item) =>
        (item.state && item.state.toLowerCase().includes(q)) ||
        (item.location && item.location.toLowerCase().includes(q))
    );
  }, [locationModalData, locationSearchQuery]);

  return (
    <div className="w-full p-4 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <Typography variant="h6" fontWeight="bold">Tasks</Typography>
      </div>
      
      <div className="h-[500px] w-full">
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          pageSizeOptions={[5, 8, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 8, page: 0 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              color: '#495057',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
          }}
        />
      </div>

      {/* ── ALL LOCATIONS BREAKDOWN MODAL ──────────────────────────────── */}
      <Modal
        open={Boolean(locationModalData)}
        onCancel={() => {
          setLocationModalData(null);
          setLocationSearchQuery("");
        }}
        footer={null}
        width={650}
        title={
          <div className="flex items-center gap-2 text-base font-bold text-gray-800 border-b border-gray-100 pb-3">
            <EnvironmentOutlined className="text-blue-600" />
            <span>Assigned Locations & Branches</span>
          </div>
        }
      >
        {locationModalData && (
          <div className="pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
              <div>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider block">
                  Task Name
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {locationModalData.taskName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Tag color="blue" className="!m-0 text-xs font-semibold">
                  {[...new Set(locationModalData.locations.map((l) => l.state?.trim()).filter(Boolean))].length} States
                </Tag>
                <Tag color="cyan" className="!m-0 text-xs font-semibold">
                  {locationModalData.locations.filter((l) => l.location?.trim() || l.state?.trim()).length} Branches
                </Tag>
              </div>
            </div>

            {/* Quick Search */}
            <div className="mb-3">
              <Input
                placeholder="Search state or city..."
                prefix={<SearchOutlined className="text-gray-400" />}
                value={locationSearchQuery}
                onChange={(e) => setLocationSearchQuery(e.target.value)}
                allowClear
                className="rounded-lg"
              />
            </div>

            {/* List Table */}
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 text-gray-600 sticky top-0 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-2.5 px-3 w-12 font-bold text-center">#</th>
                    <th className="py-2.5 px-3 font-bold">State</th>
                    <th className="py-2.5 px-3 font-bold">Location / Branch City</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredModalLocations.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-6 text-gray-400 italic">
                        {locationSearchQuery ? "No locations matching your search." : "No locations recorded."}
                      </td>
                    </tr>
                  ) : (
                    filteredModalLocations.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-3 text-center text-gray-400 font-medium">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-3">
                          <Tag color="blue" className="!m-0 font-medium">
                            {item.state || "Unspecified"}
                          </Tag>
                        </td>
                        <td className="py-2.5 px-3 font-medium text-gray-700">
                          {item.location ? (
                            <Tag color="cyan" className="!m-0">
                              {item.location}
                            </Tag>
                          ) : (
                            <span className="text-gray-400 italic">All branches</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MemberTaskHome;